// @flow

import { BrowserDetection } from '@jitsi/js-utils';
import React, { memo } from 'react';

import { FIREFOX, SAFARI } from '../../../../../browser';
import { translate } from '../../../base/i18n';
import {
    IconClickPledge,
    IconCodeBlock,
    IconDonate,
    IconFeedback,
    IconOpenInNew,
    IconPresentation,
    IconVoiceCommand
} from '../../../base/icons';
import { OverflowMenuItem } from '../../../base/toolbox/components';
import { isVpaasMeeting } from '../../../billing-counter/functions';
import NoiseSuppressionButton from '../../../noise-suppression/components/NoiseSuppressionButton';
import {
    LiveStreamButton,
    RecordButton,
    LocalRecordButton as RecordingLocalRecordButton
} from '../../../recording';
import {
    OverflowMenuVideoQualityItem
} from '../../../video-quality';
import { VideoBackgroundButton } from '../../../virtual-background';
import { checkBlurSupport } from '../../../virtual-background/functions';
import DownloadButton from '../DownloadButton';
import HelpButton from '../HelpButton';

import OverflowMenuProfileItem from './OverflowMenuProfileItem';

type Props = {

    // Visibility flags
    _clicknPledgeVisible: boolean,
    _donorBoxVisible: boolean,
    _feedbackConfigured: boolean,
    _screensharing: boolean,
    _geturl: string,
    clickAndPledgeUrl: string,
    _isVoiceCommandDisabled: boolean,
    _isModerator: boolean,
    _isGuest: boolean,
    _isVpaasMeeting: boolean,
    _embedMeeting: boolean,
    liveStreamVisible: boolean,
    RecordingVisible: boolean,
    vcVisible: boolean,
    desktopApp: boolean,
    _GiventOwnerId: ?string,
    _clickandpledGUID: ?string,
    _localParticipantID: string,
    _isRecordingRunning: boolean,
    _isLiveStreamRunning: boolean,

    // Callbacks (passed from Toolbox)
    onToggleProfile: Function,
    onOpenVideoQuality: Function,
    onOpenSpeakerStats: Function,
    onOpenEmbedMeeting: Function,
    onOpenFeedback: Function,
    onOpenKeyboardShortcuts: Function,
    onToggleDonorbox: Function,
    onRemoveDonorbox: Function,
    onToggleClickAndPledge: Function,
    onRemoveClickAndPledge: Function,
    onToggleVoiceCommands: Function,

    dispatch: Function,
    t: Function,
};

/**
 * Renders the overflow menu item list.
 * Wrapped in React.memo so it only re-renders when its own props change.
 * Because `t` is a new function reference when language changes (via translate HOC),
 * the memo comparison will automatically bust the cache and re-render with fresh labels.
 */
const OverflowMenuContent = memo(({
    _clicknPledgeVisible,
    _donorBoxVisible,
    _feedbackConfigured,
    _screensharing,
    _geturl,
    clickAndPledgeUrl,
    _isVoiceCommandDisabled,
    _isModerator,
    _isGuest,
    _embedMeeting,
    liveStreamVisible,
    RecordingVisible,
    desktopApp,
    _GiventOwnerId,
    _clickandpledGUID,
    _localParticipantID,
    t,
    onToggleProfile,
    onOpenVideoQuality,
    onOpenSpeakerStats,
    onOpenEmbedMeeting,
    onOpenFeedback,
    onOpenKeyboardShortcuts,
    onToggleDonorbox,
    onRemoveDonorbox,
    onToggleClickAndPledge,
    _visibleButtons,
    onRemoveClickAndPledge,
    onToggleVoiceCommands
}: Props) => {
    const bowser = new BrowserDetection();
    const browserName = bowser.getName();


    const isEmbedMeetingVisible = () => !isVpaasMeeting && (_embedMeeting || _visibleButtons.has('embedmeeting'));
    const isProfileVisible = () => _isGuest && _visibleButtons.has('profile');

    const menuItems = [];

    if (isProfileVisible()) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'profile'>
                <OverflowMenuProfileItem
                    onClick = { onToggleProfile } />
            </div>
        );
    }

    if (_visibleButtons.has('videoquality')) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'videoquality'>
                <OverflowMenuVideoQualityItem
                    onClick = { onOpenVideoQuality } />
            </div>
        );
    }

    if (liveStreamVisible) {
        menuItems.push(
            <div
                className = 'col-m-4'
                id = 'liveStream-div'
                key = 'livestreaming'>
                <LiveStreamButton showLabel = { true } />
            </div>
        );
    }

    if (_visibleButtons.has('microphone')) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'noise-suppression'>
                <NoiseSuppressionButton showLabel = { true } />
            </div>
        );
    }

    if (RecordingVisible) {
        menuItems.push(
            <div
                className = 'col-m-4'
                id = 'recordIng-div'
                key = 'record'>
                <RecordButton showLabel = { true } />
            </div>
        );
    }

    if (_visibleButtons.has('localrecording') && _isModerator && !desktopApp && browserName !== (SAFARI || FIREFOX)) {
        menuItems.push(
            <div
                className = 'col-m-4'
                id = 'localRecording-div'
                key = 'localrecording'>
                <RecordingLocalRecordButton showLabel = { true } />
            </div>
        );
    }

    if ((_visibleButtons.has('addclickNpledge') || _clicknPledgeVisible) && _isModerator) {
        const hasActive = (_GiventOwnerId === _localParticipantID) || _clickandpledGUID !== '';

        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'clickandpledge'>
                <OverflowMenuItem
                    accessibilityLabel = { hasActive
                        ? t('toolbar.accessibilityLabel.removeDonation')
                        : t('toolbar.accessibilityLabel.donationLink') }
                    icon = { IconClickPledge }
                    onClick = { hasActive ? onRemoveClickAndPledge : onToggleClickAndPledge }
                    text = { hasActive ? t('toolbar.rmoveCDonation') : t('toolbar.donationCLP') } />
            </div>
        );
    }

    if ((_visibleButtons.has('adddonorbox') || _donorBoxVisible)
        && (clickAndPledgeUrl === undefined)
        && _isModerator) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'donorbox'>
                <OverflowMenuItem
                    accessibilityLabel = { _geturl === undefined
                        ? t('toolbar.accessibilityLabel.donationLink')
                        : t('toolbar.accessibilityLabel.removeDonation') }
                    disabled = { !(clickAndPledgeUrl === undefined) }
                    icon = { IconDonate }
                    onClick = { _geturl === undefined ? onToggleDonorbox : onRemoveDonorbox }
                    text = { _geturl === undefined ? t('toolbar.donationLink') : t('toolbar.removeDonation') } />
            </div>
        );
    }

    if (_visibleButtons.has('voicecommand') && !_isVoiceCommandDisabled) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'voicecommand'>
                <OverflowMenuItem
                    accessibilityLabel = { t('toolbar.voiceCommand') }
                    icon = { IconVoiceCommand }
                    onClick = { onToggleVoiceCommands }
                    text = { t('toolbar.voiceCommand') } />
            </div>
        );
    }

    if ((_visibleButtons.has('select-background') || _visibleButtons.has('videobackgroundblur'))
        && browserName !== SAFARI) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'select-background'>
                <VideoBackgroundButton
                    showLabel = { true }
                    visible = { !_screensharing && checkBlurSupport() } />
            </div>
        );
    }

    if (_visibleButtons.has('stats')) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'stats'>
                <OverflowMenuItem
                    accessibilityLabel = { t('toolbar.accessibilityLabel.speakerStats') }
                    icon = { IconPresentation }
                    onClick = { onOpenSpeakerStats }
                    text = { t('toolbar.speakerStats') } />
            </div>
        );
    }

    if (isEmbedMeetingVisible() && _isModerator) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'embed'>
                <OverflowMenuItem
                    accessibilityLabel = { t('toolbar.accessibilityLabel.embedMeeting') }
                    icon = { IconCodeBlock }
                    onClick = { onOpenEmbedMeeting }
                    text = { t('toolbar.embedMeeting') } />
            </div>
        );
    }

    if (_visibleButtons.has('feedback') && _feedbackConfigured) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'feedback'>
                <OverflowMenuItem
                    accessibilityLabel = { t('toolbar.accessibilityLabel.feedback') }
                    icon = { IconFeedback }
                    onClick = { onOpenFeedback }
                    text = { t('toolbar.feedback') } />
            </div>
        );
    }

    if (_visibleButtons.has('shortcuts')) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'shortcuts'>
                <OverflowMenuItem
                    accessibilityLabel = { t('toolbar.accessibilityLabel.shortcuts') }
                    icon = { IconOpenInNew }
                    onClick = { onOpenKeyboardShortcuts }
                    text = { t('toolbar.shortcuts') } />
            </div>
        );
    }

    if (_visibleButtons.has('download')) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'download'>
                <DownloadButton showLabel = { true } />
            </div>
        );
    }

    if (_visibleButtons.has('help')) {
        menuItems.push(
            <div
                className = 'col-m-4'
                key = 'help'>
                <HelpButton showLabel = { true } />
            </div>
        );
    }

    return menuItems;
});

OverflowMenuContent.displayName = 'OverflowMenuContent';

export default translate(OverflowMenuContent);
